import { useState } from 'react';
import {
  App,
  Button,
  Collapse,
  Drawer,
  Empty,
  InputNumber,
  Popconfirm,
  Spin,
  Switch,
  Tag,
} from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { MockServer, MockServerRoute } from '@core/entities/MockServer';
import type { MockRoutePayload } from '@core/repositories/MockServerRepository';
import { NamePromptModal } from '@presentation/components/workbench/NamePromptModal';
import { MockRouteFormModal } from '@presentation/components/workbench/MockRouteFormModal';
import {
  useCreateMockRoute,
  useCreateMockServer,
  useDeleteMockRoute,
  useDeleteMockServer,
  useMockServers,
  useUpdateMockRoute,
  useUpdateMockServer,
} from '@presentation/hooks/useMockServers';

interface MockServerDrawerProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

const RouteRow = ({
  route,
  onEdit,
  onDelete,
}: {
  route: MockServerRoute;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <div className="flex items-center gap-2 border-b border-black/5 py-1.5 last:border-0 dark:border-white/10">
    <Tag className="w-16 shrink-0 text-center font-mono text-[11px]">{route.method}</Tag>
    <span className="min-w-0 flex-1 truncate font-mono text-xs">{route.path}</span>
    <Tag color={route.statusCode < 400 ? 'green' : 'red'} className="shrink-0">
      {route.statusCode}
    </Tag>
    {!route.isEnabled && <Tag className="shrink-0">disabled</Tag>}
    <Button
      size="small"
      type="text"
      icon={<EditOutlined />}
      onClick={onEdit}
      aria-label="Edit route"
    />
    <Popconfirm title="Delete this route?" onConfirm={onDelete}>
      <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Delete route" />
    </Popconfirm>
  </div>
);

const MockServerPanel = ({
  workspaceId,
  mockServer,
}: {
  workspaceId: string;
  mockServer: MockServer;
}) => {
  const { message } = App.useApp();
  const updateServer = useUpdateMockServer(workspaceId);
  const deleteServer = useDeleteMockServer(workspaceId);
  const createRoute = useCreateMockRoute(workspaceId);
  const updateRoute = useUpdateMockRoute(workspaceId);
  const deleteRoute = useDeleteMockRoute(workspaceId);

  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<MockServerRoute | null>(null);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(mockServer.fullUrl);
      void message.success('URL copied');
    } catch {
      void message.error('Could not copy — your browser blocked clipboard access');
    }
  };

  const handleRouteSubmit = (payload: MockRoutePayload) => {
    if (editingRoute) {
      updateRoute.mutate(
        { routeId: editingRoute.id, payload },
        {
          onSuccess: () => {
            void message.success('Route saved');
            setRouteModalOpen(false);
          },
        },
      );
    } else {
      createRoute.mutate(
        { mockServerId: mockServer.id, payload },
        {
          onSuccess: () => {
            void message.success('Route added');
            setRouteModalOpen(false);
          },
        },
      );
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Switch
          checked={mockServer.isActive}
          onChange={(checked) =>
            updateServer.mutate({ id: mockServer.id, payload: { isActive: checked } })
          }
        />
        <span className="text-xs text-gray-500">{mockServer.isActive ? 'Active' : 'Inactive'}</span>

        <span className="mx-1 text-gray-300">|</span>

        <span className="text-xs text-gray-500">Delay</span>
        <InputNumber
          size="small"
          min={0}
          max={10000}
          value={mockServer.delayMs}
          onChange={(value) =>
            updateServer.mutate({ id: mockServer.id, payload: { delayMs: value ?? 0 } })
          }
          className="w-20"
        />
        <span className="text-xs text-gray-500">ms</span>

        {mockServer.abilities.delete && (
          <Popconfirm
            title="Delete this mock server?"
            description="This also deletes all of its routes."
            onConfirm={() => deleteServer.mutate(mockServer.id)}
          >
            <Button size="small" danger type="text" icon={<DeleteOutlined />} className="ml-auto">
              Delete
            </Button>
          </Popconfirm>
        )}
      </div>

      <div className="flex items-center gap-2 rounded border border-black/10 bg-black/[0.02] px-2 py-1.5 dark:border-white/10 dark:bg-white/[0.03]">
        <span className="min-w-0 flex-1 truncate font-mono text-xs">{mockServer.fullUrl}</span>
        <Button
          size="small"
          type="text"
          icon={<CopyOutlined />}
          onClick={() => void copyUrl()}
          aria-label="Copy URL"
        />
      </div>

      <div>
        {mockServer.routes.length === 0 && (
          <Empty
            description="No routes yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            styles={{ image: { height: 32 } }}
          />
        )}
        {mockServer.routes.map((route) => (
          <RouteRow
            key={route.id}
            route={route}
            onEdit={() => {
              setEditingRoute(route);
              setRouteModalOpen(true);
            }}
            onDelete={() => deleteRoute.mutate(route.id)}
          />
        ))}
      </div>

      <Button
        size="small"
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => {
          setEditingRoute(null);
          setRouteModalOpen(true);
        }}
        className="self-start"
      >
        Add route
      </Button>

      <MockRouteFormModal
        open={routeModalOpen}
        route={editingRoute}
        confirmLoading={createRoute.isPending || updateRoute.isPending}
        onSubmit={handleRouteSubmit}
        onCancel={() => setRouteModalOpen(false)}
      />
    </div>
  );
};

export const MockServerDrawer = ({ workspaceId, open, onClose }: MockServerDrawerProps) => {
  const { message } = App.useApp();
  const { data: mockServers, isLoading } = useMockServers(workspaceId);
  const createServer = useCreateMockServer(workspaceId);
  const [creating, setCreating] = useState(false);

  return (
    <Drawer title="Mock Servers" open={open} onClose={onClose} size={760}>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Define routes and responses, then point any HTTP client at the mock server&apos;s URL — no
          auth required to call it.
        </span>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setCreating(true)}
          className="shrink-0"
        >
          New mock server
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-6">
          <Spin size="small" />
        </div>
      )}

      {!isLoading && mockServers?.length === 0 && (
        <Empty description="No mock servers yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

      {mockServers && mockServers.length > 0 && (
        <Collapse
          items={mockServers.map((mockServer) => ({
            key: mockServer.id,
            label: (
              <span className="flex items-center gap-2">
                <span className="font-medium">{mockServer.name}</span>
                <Tag className="shrink-0">
                  {mockServer.routeCount ?? mockServer.routes.length} routes
                </Tag>
                {!mockServer.isActive && <Tag color="default">inactive</Tag>}
              </span>
            ),
            children: <MockServerPanel workspaceId={workspaceId} mockServer={mockServer} />,
          }))}
        />
      )}

      <NamePromptModal
        open={creating}
        title="New mock server"
        confirmLoading={createServer.isPending}
        onCancel={() => setCreating(false)}
        onSubmit={(name) =>
          createServer.mutate(
            { name },
            {
              onSuccess: () => {
                void message.success('Mock server created');
                setCreating(false);
              },
            },
          )
        }
      />
    </Drawer>
  );
};
